<script lang="ts">
  import { onMount } from "svelte";
  import { appStore } from "../stores/appStore";
  import { getTokenStorage } from "../utils/localStorage";
  import { AppApiClient } from "../api_contracts/app.client";

  import { RepositoryInfo, UserProfile } from "../api_contracts/app";

  let client: AppApiClient | null = $state(null);
  let profile: UserProfile | null = $state(null);
  let token: string = $state("");
  let repositories: RepositoryInfo[] = $state([]);
  let loading: boolean = $state(true);
  let error: string = $state("");

  // Subscribe to store
  appStore.subscribe((state) => {
    client = state.client;
    profile = state.profile;
    token = state.token;
  });

  // Helper to format relative time
  function formatRelativeTime(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) return "just now";
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
    if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
    return `${years} year${years === 1 ? "" : "s"} ago`;
  }

  async function fetchRecentRepositories() {
    if (!client || !token) {
      loading = false;
      return;
    }

    try {
      loading = true;
      error = "";

      const result = await client.getUserRepositories({
        token,
        userName: "", // Empty means get current user's repositories
      });

      if (result.response.successFull && result.response.repositories) {
        // Sort by updatedAt descending and take first 5
        const sortedRepos = [...result.response.repositories]
          .sort((a, b) => {
            const timeA = Number(a.updatedAt?.seconds || 0);
            const timeB = Number(b.updatedAt?.seconds || 0);
            return timeB - timeA;
          })
          .slice(0, 5);

        repositories = sortedRepos.map((repo) => ({
          name: repo.name,
          description: repo.description || "",
          visibility: repo.visibility,
          language: repo.primaryLanguage || "",
          languageColor: repo.primaryLanguageColor || "#cccccc",
          stars: repo.starCount,
          updatedAt: formatRelativeTime(
            new Date(Number(repo.updatedAt?.seconds || 0) * 1000),
          ),
        }));
      } else {
        error = result.response.reason || "Failed to fetch repositories";
      }
    } catch (e: any) {
      error = e.message || "Error fetching repositories";
      console.error("Error fetching repositories:", e);
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    // Check if authenticated
    const storedToken = getTokenStorage();
    if (storedToken) {
      fetchRecentRepositories();
    } else {
      loading = false;
    }
  });
</script>

<div
  class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
>
  <div
    class="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center"
  >
    <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
      Recent Repositories
    </h2>
    <a
      href="#/repositories"
      class="text-sm text-indigo-600 dark:text-blue-400 hover:text-indigo-700 dark:hover:text-blue-300 font-medium"
    >
      View all
    </a>
  </div>

  <div class="divide-y divide-gray-200 dark:divide-gray-700">
    {#if loading}
      <div class="p-12 text-center">
        <div
          class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
        ></div>
        <p class="mt-4 text-gray-600 dark:text-gray-400">
          Loading repositories...
        </p>
      </div>
    {:else if error}
      <div class="p-12 text-center">
        <p class="text-red-600 dark:text-red-400">{error}</p>
      </div>
    {:else if repositories.length === 0}
      <div class="p-12 text-center">
        <p class="text-gray-600 dark:text-gray-400">No repositories found</p>
        <a
          href="#/repositories/new"
          class="mt-4 inline-block text-blue-600 dark:text-blue-400 hover:underline"
        >
          Create your first repository
        </a>
      </div>
    {:else}
      {#each repositories as repo}
        <div
          role="button"
          tabindex="0"
          onclick={() => (window.location.hash = `#/users/${profile?.userName}/repositories/${repo.name}`)}
          onkeydown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              window.location.hash = `#/users/${profile?.userName}/repositories/${repo.name}`;
            }
          }}
          class="block p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1 min-w-0">
              <div class="flex items-center space-x-2 mb-2">
                <h3
                  class="text-base font-semibold text-gray-900 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-blue-400"
                >
                  {repo.name}
                </h3>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium {repo.visibility ===
                  'public'
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 border border-green-200 dark:border-green-800'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600'}"
                >
                  {repo.visibility}
                </span>
              </div>
              {#if repo.description}
                <p class="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  {repo.description}
                </p>
              {/if}
              <div
                class="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400"
              >
                {#if repo.language}
                  <span class="flex items-center space-x-1">
                    <span
                      class="w-3 h-3 rounded-full"
                      style="background-color: {repo.languageColor}"
                    ></span>
                    <span>{repo.language}</span>
                  </span>
                {/if}
                <span class="flex items-center space-x-1">
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                    />
                  </svg>
                  <span>{repo.stars}</span>
                </span>
                <span>Updated {repo.updatedAt}</span>
              </div>
            </div>
            <svg
              class="w-5 h-5 text-gray-400 dark:text-gray-500 ml-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      {/each}
    {/if}
  </div>
</div>
